import { Button } from '@mm/components';
import React, { useState } from 'react';
import { ReactComponent as GrowthIcon } from './images/growth.svg';
import { ReactComponent as MailboxIcon } from './images/mailbox.svg';
import { ReactComponent as RouteIcon } from './images/route.svg';
import { ReactComponent as PieChartIcon } from './images/pie-chart.svg';
import advancedAnalyticsSrc from './images/advanced-analytics.png';
import audienceSegmentationSrc from './images/audience-segmentation.png';
import emailDeliverabilitySrc from './images/email-deliverability.png';
import leadScoringSrc from './images/lead-scoring.png';
import { LLRActionButtons } from '../LLRActionButtons/LLRActionButtons';
import styles from './Features.module.css';
import classNames from 'classnames';

export const Features = ({
  websiteURL,
  onClick,
  isWhiteLabel,
}: {
  websiteURL: string;
  onClick: () => void;
  isWhiteLabel?: boolean;
}) => {
  const [activeButton, setActiveButton] = useState('customerJourney');

  const featureButtons = [
    {
      id: 'customerJourney',
      title: 'Customer Journey',
      icon: <RouteIcon />,
    },
    {
      id: 'audienceSegmentation',
      title: 'Audience Segmentation',
      icon: <PieChartIcon />,
    },
    {
      id: 'advancedAnalytics',
      title: 'Advanced Analytics',
      icon: <GrowthIcon />,
    },
    {
      id: 'emailDeliverability',
      title: 'Email deliverability tools',
      icon: <MailboxIcon />,
    },
  ];

  const data = {
    customerJourney: {
      subtitle: 'Get back the attribution data you thought was lost',
      description: (
        <div className={styles.description}>
          Tracking the customer journey used to be easy but with privacy updates
          and the removal of cookies, cross-channel tracking is more complicated
          than ever.
          <div>
            With Customers.ai you can get back the customer journey insights you
            once had. See page paths, email opens, cart abandonments, and more!
          </div>
        </div>
      ),
      image: leadScoringSrc,
    },
    audienceSegmentation: {
      subtitle: 'Build custom audiences in seconds',
      description: (
        <div className={styles.description}>
          Our advanced audience segmentation capabilities let you create
          audiences based on hundreds of attributes, including location,
          demographics, behavioral data, email activity, website activity,
          channel & more!
        </div>
      ),
      image: audienceSegmentationSrc,
    },
    advancedAnalytics: {
      subtitle: 'Track contact performance across your marketing channels',
      description: (
        <div className={styles.description}>
          We give you more than just contact data. Get demographic information,
          website intent data, and see how your ads and email audiences are
          performing - all in one place.
        </div>
      ),
      image: advancedAnalyticsSrc,
    },
    emailDeliverability: {
      subtitle:
        'Test your email deliverability and find out where your messages are landing',
      description: (
        <div className={styles.description}>
          Make sure your messages are hitting the inbox and not the spam folder
          or promotions tab.
        </div>
      ),
      image: emailDeliverabilitySrc,
    },
  };

  return (
    <div className={styles.container}>
      <h2 className={styles.heading}>
        The <span className={styles.titleKalam}>ONLY ID resolution</span>{' '}
        company with <span className={styles.titleKalam}>VALIDATED</span>{' '}
        correct email addresses of your anonymous visitors
      </h2>
      <div className={styles.content}>
        <div className={styles.leftBlock}>
          <h3 className={styles.subheading}>Get more with Customers.ai</h3>
          {featureButtons.map(button => (
            <Button
              key={button.title}
              color="transparent"
              css={{
                container: classNames(styles.featureButton, {
                  [styles.active]: activeButton === button.id,
                }),
              }}
              onClick={() => setActiveButton(button.id)}
            >
              <span className={styles.buttonIcon}>{button.icon}</span>
              {button.title}
            </Button>
          ))}
          {!isWhiteLabel && (
            <LLRActionButtons
              buttonName="Get Started Free"
              websiteURL={websiteURL}
              withDemoButton={false}
              onClick={onClick}
              className={styles.actionButton}
            />
          )}
        </div>
        <div className={styles.rightBlock}>
          <h3 className={styles.subheading}>{data[activeButton]?.subtitle}</h3>
          {data[activeButton]?.description}
          <div className={styles.imageContainer}>
            <img
              src={data[activeButton]?.image}
              className={styles.featureImage}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
